import React, { useRef, useState, useEffect } from "react";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardHeader,
  MDBCardBody,
  MDBCardFooter,
  MDBIcon,
  MDBBtn,
} from "mdb-react-ui-kit";
import { MDBTooltip } from "mdb-react-ui-kit";

import { systemPrompt } from "./SysPrompt";

export default function App() {
  const [apiKey, setApiKey] = useState("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");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);


  // --- Recording state ---
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const chunksRef = useRef([]);

  // --- TTS: track voices & which msg is speaking ---
  const [availableVoices, setAvailableVoices] = useState([]);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);

  // --------------------------------------------------
  // 0) Load Voices on Mount
  // --------------------------------------------------
  useEffect(() => {
    const loadVoices = () => {
      const voices = speechSynthesis.getVoices();
      setAvailableVoices(voices);
    };
    loadVoices();
    speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  // --------------------------------------------------
  // TTS: Toggle Play/Mute
  // --------------------------------------------------
  const toggleSpeech = (msg, index) => {
    if (speakingMessageId === index) {
      // Already speaking this msg -> Mute
      speechSynthesis.cancel();
      setSpeakingMessageId(null);
    } else {
      // Cancel anything else, speak this one
      speechSynthesis.cancel();
      setSpeakingMessageId(index);

      const utterance = new SpeechSynthesisUtterance(msg.content);
      utterance.lang = "en-US";
      if (availableVoices.length > 0) {
        utterance.voice = availableVoices[0];
      }
      utterance.onend = () => {
        setSpeakingMessageId(null);
      };
      speechSynthesis.speak(utterance);
    }
  };

  // --------------------------------------------------
  // 1) Start Recording (on press down)
  // --------------------------------------------------
  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert("Browser does not support audio recording.");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/mp4"; // fallback for iOS Safari

      const recorder = new MediaRecorder(stream, { mimeType });

      // Collect data chunks
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      // On stop, we transcribe & auto-send
      recorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        chunksRef.current = [];
        setProcessing(true);

        // 1) Transcribe
        const text = await transcribeAudio(audioBlob);
        if (text) {
          // 2) Auto-send the transcribed text to GPT
          handleSendAuto(text);
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.error("Error starting recording:", err);
      alert("Could not start recording. Check mic permissions or HTTPS.");
    }
  };

  // --------------------------------------------------
  // 2) Stop Recording (on release)
  // --------------------------------------------------
  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // --------------------------------------------------
  // 3) Transcribe Audio (returns text)
  // --------------------------------------------------
  const transcribeAudio = async (audioBlob) => {
    if (!apiKey) {
      alert("Please provide an OpenAI API key to use Whisper.");
      return "";
    }

    const formData = new FormData();
    formData.append("file", audioBlob, "audio.webm");
    formData.append("model", "whisper-1");

    try {
      const response = await fetch("https://api.openai.com/v1/audio/transcriptions", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${apiKey}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.text) {
        setProcessing(false);
        return data.text;
      } else {
        console.error("Transcription failed:", data);
        alert("Transcription failed. Check console for details.");
        return "";
      }
    } catch (error) {
      console.error("Error transcribing audio:", error);
      alert("Error transcribing audio. Check console for details.");
      return "";
    }
  };

  // --------------------------------------------------
  // 4) Send text to GPT
  // --------------------------------------------------
  const sendToOpenAI = async (userMsg) => {
    try {
      const response = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "gpt-3.5-turbo",
          messages: [{ role: "system", content: systemPrompt }, ...messages, userMsg],
        }),
      });

      const data = await response.json();
      const aiReply = data.choices?.[0]?.message;
      if (aiReply) {
        const newIndex = messages.length; // index of new message
        setMessages((prev) => [...prev, aiReply]);

        // Auto-play new assistant message
        setTimeout(() => {
          toggleSpeech(aiReply, newIndex);
        }, 0);
      }
    } catch (error) {
      console.error("Error calling OpenAI:", error);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // 5) Handle Send (manual text from input)
  // --------------------------------------------------
  const handleSend = () => {
    if (!input.trim() || !apiKey) return;

    const userMsg = { role: "user", content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    sendToOpenAI(userMsg);
  };

  // 5b) Handle Send for auto text from transcription
  const handleSendAuto = (text) => {
    if (!text.trim() || !apiKey) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    sendToOpenAI(userMsg);
  };

  return (
    <MDBContainer
      fluid
      className="m-0 p-0"
      style={{
        height: "100vh",
        backgroundColor: "#eee",
      }}
    >
      <MDBRow className="h-100 m-0">
        <MDBCol xs="12" className="h-100 p-0">
          <MDBCard
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "0",
            }}
          >
            {/* HEADER */}
            <MDBCardHeader className="d-flex justify-content-between align-items-center p-3">
              <div className="d-flex align-items-center">
                <img
                  src="/img/logo.jpeg"
                  alt="Logo"
                  style={{ width: "40px", height: "40px", marginRight: "8px" }}
                />
                <h5 className="mb-0">CyAi</h5>
              </div>
              <MDBBtn color="primary" size="sm" rippleColor="dark">
                Gimatex Industries
              </MDBBtn>
            </MDBCardHeader>

            {/* BODY (SCROLLABLE) */}
            <MDBCardBody style={{ flex: 1, overflowY: "auto" }}>
              {messages.map((msg, i) => {
                const isUser = msg.role === "user";
                const isAssistant = msg.role === "assistant";
                const isSpeaking = speakingMessageId === i;

                return (
                  <div
                    key={i}
                    className={`d-flex flex-row justify-content-${isUser ? "end" : "start"
                      } mb-4`}
                  >
                    {/* Assistant avatar */}
                    {isAssistant && (
                      <img
                        src="/img/robot.png"
                        alt="assistant avatar"
                        style={{ width: "45px", height: "45px" }}
                      />
                    )}

                    <div>
                      <p
                        className={`small p-2 ${isUser ? "me-3 text-white bg-primary" : "ms-3"
                          } mb-1 rounded-3`}
                        style={{
                          backgroundColor: isUser ? "" : "#f5f6f7",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {msg.content}
                      </p>
                      {isAssistant && (
                        <button
                          className="btn btn-link p-0 ms-3 text-muted"
                          onClick={() => toggleSpeech(msg, i)}
                        >
                          {isSpeaking ? (
                            <>
                              <MDBIcon fas icon="volume-mute" /> Mute
                            </>
                          ) : (
                            <>
                              <MDBIcon fas icon="play" /> Play
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* User avatar */}
                    {isUser && (
                      <img
                        src="/img/user.png"
                        alt="user avatar"
                        style={{ width: "45px", height: "45px" }}
                      />
                    )}
                  </div>
                );
              })}

              {loading && (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </MDBCardBody>

            {/* FOOTER */}
            <MDBCardFooter className="text-muted d-flex justify-content-start align-items-center p-3" style={{ position: "relative", overflow: "visible" }}>
              {/* Avatar */}
              <img
                src="/img/user.png"
                alt="avatar"
                style={{ width: "45px", height: "45px" }}
              />

              {/* Text Input */}
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder={
                  processing
                    ? "Processing..."
                    : isRecording
                      ? "Listening..."
                      : "Type message"
                }
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                style={{ marginLeft: "10px", marginRight: "10px" }}
              />

              {/* Press & Hold Microphone */}
              {showTooltip && (
                <span
                  style={{
                    position: "absolute",
                    top: "-35px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#000",
                    color: "#fff",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                    zIndex: 9999, // <-- add a high z-index
                    
                  }}
                >
                  Push to talk
                </span>
              )}
              <MDBBtn
                color={isRecording ? "danger" : "light"}
                className="rounded-circle p-2"
                style={{ width: "4rem", height: "3rem", margin: "10px" }}
                onMouseDown={(e) => {
                  setShowTooltip(false); // hide tooltip after first press
                  startRecording();      // your existing logic
                }}
                onMouseUp={stopRecording}
                onTouchStart={(e) => {
                  setShowTooltip(false);
                  startRecording();
                }}
                onTouchEnd={stopRecording}
              >
                <MDBIcon fas icon="microphone" />
              </MDBBtn>



              {/* Send Button for typed messages */}
              <MDBBtn
                color="light"
                className="rounded-circle p-2"
                style={{ width: "4rem", height: "3rem" }}
                onClick={handleSend}>
                <MDBIcon fas icon="paper-plane" />
              </MDBBtn>
            </MDBCardFooter>
          </MDBCard>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  );
}
